import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environment/environment';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable({
	providedIn: 'root',
})
export class UserService {
	private baseUrl = `${environment.pathBackend}/users`;

	constructor(private http: HttpClient) {}

	/**
	 * Get all users with pagination and filters
	 */
	getAllUsers(params: any): Observable<any> {
		let httpParams = new HttpParams();
		Object.keys(params).forEach((key) => {
			if (params[key] !== undefined && params[key] !== null) {
				// Convert all values to string for proper HTTP param handling
				httpParams = httpParams.set(key, String(params[key]));
			}
		});

		return this.http.get<any>(this.baseUrl, { params: httpParams }).pipe(
			catchError(() => {
				return of({ data: [], total: 0 });
			}),
		);
	}

	/**
	 * Get minimal users list for table views (optimized endpoint)
	 * Returns only essential fields: id, name, lastName, lastName2, email, banned, emailVerified, mfaEnabled, profilePhotoUrl, role.name
	 */
	getUsersMinimal(params: any): Observable<any> {
		let httpParams = new HttpParams();
		Object.keys(params).forEach((key) => {
			if (params[key] !== undefined && params[key] !== null) {
				httpParams = httpParams.set(key, String(params[key]));
			}
		});

		return this.http.get<any>(`${this.baseUrl}/list`, { params: httpParams }).pipe(
			catchError(() => {
				return of({ data: [], total: 0 });
			}),
		);
	}

	/**
	 * Retrieves a paginated list of users from the backend with optional filters.
	 * @returns An Observable containing the filtered and paginated user data.
	 */
	getUsers(page: number, limit: number, sortBy?: string, sortOrder?: string, role?: string): Observable<any> {
		let params = new HttpParams().set('page', page.toString()).set('limit', limit.toString());

		if (sortBy) params = params.set('sort_by', sortBy);
		if (sortOrder) params = params.set('sort_order', sortOrder);
		if (role) params = params.set('role', role);

		return this.http.get<any>(this.baseUrl, { params }).pipe(
			catchError(() => {
				return throwError(() => new Error('Failed to fetch users'));
			}),
		);
	}

	/**
	 * Retrieves a user by its ID.
	 */
	getUserById(id: string): Observable<any> {
		return this.http.get<any>(`${this.baseUrl}/${id}`).pipe(
			catchError(() => {
				return throwError(() => new Error('Failed to fetch user'));
			}),
		);
	}

	/**
	 * Retrieves minimal user info by ID (optimized for frequent requests).
	 * Returns only essential data without relations or Supabase calls.
	 * @param id User ID
	 * @returns Observable with minimal user data
	 */
	getUserByIdMinimal(id: string | number): Observable<any> {
		return this.http.get<any>(`${this.baseUrl}/${id}/minimal`).pipe(
			catchError(() => {
				return throwError(() => new Error('Failed to fetch minimal user'));
			}),
		);
	}

	/**
	 * Get user ID by email
	 * @param email User email
	 * @returns Observable with user Supabase ID and database ID (if exists)
	 */
	getUserIdByEmail(email: string): Observable<{ id: string; userId?: number }> {
		return this.http
			.get<{ id: string; userId?: number }>(`${this.baseUrl}/email/${encodeURIComponent(email)}/id`)
			.pipe(
				catchError(() => {
					return throwError(() => new Error('Failed to load user ID'));
				}),
			);
	}

	/**
	 * Creates a new user.
	 */
	createUser(userData: any): Observable<any> {
		return this.http.post<any>(this.baseUrl, userData).pipe(
			catchError(() => {
				return throwError(() => new Error('El usuario ya existe'));
			}),
		);
	}

	/**
	 * Updates an existing user.
	 */
	updateUser(id: string, userData: any): Observable<any> {
		return this.http.put<any>(`${this.baseUrl}/${id}`, userData).pipe(
			catchError((error) => {
				return throwError(() => error);
			}),
		);
	}

	/**
	 * Delete user
	 */
	deleteUser(id: number): Observable<any> {
		return this.http.delete<any>(`${this.baseUrl}/${id}`).pipe(
			catchError((error) => {
				throw error;
			}),
		);
	}

	/**
	 * Deletes one or multiple users.
	 */
	deleteUsers(ids: string[]): Observable<{ success: boolean }> {
		const payload = { ids };

		return this.http
			.request<{ success: boolean }>('DELETE', this.baseUrl, {
				body: payload,
				headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
			})
			.pipe(
				catchError((error) => {
					return throwError(() => error);
				}),
			);
	}

	/**
	 * Update user password by user ID
	 * @param userId User's numeric ID
	 * @param passwordData Object containing the current and new password
	 */
	updatePassword(userId: string, passwordData: { currentPassword?: string; password: string }): Observable<void> {
		return this.http
			.put<void>(`${this.baseUrl}/${userId}/password`, {
				currentPassword: passwordData.currentPassword,
				password: passwordData.password,
			})
			.pipe(
				catchError((error) => {
					// Current password incorrect error
					if (error.status === 401) {
						return throwError(() => new Error(error.error?.message || 'Current password is incorrect'));
					}

					// Validate password format
					const passwordRegex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
					if (!passwordRegex.test(passwordData.password)) {
						return throwError(
							() =>
								new Error(
									'Password must have at least 8 characters, one uppercase, one lowercase, one number and one special character',
								),
						);
					}

					if (error.status === 400) {
						return throwError(() => new Error(error.error?.message || 'Password format error'));
					}
					return throwError(() => new Error('Error updating password'));
				}),
			);
	}

	/**
	 * Ban a user
	 * @param userId User's numeric ID
	 * @returns Observable with success message
	 */
	banUser(userId: number): Observable<{ success: boolean; message: string }> {
		return this.http.post<{ success: boolean; message: string }>(`${this.baseUrl}/${userId}/ban`, {}).pipe(
			catchError((error) => {
				return throwError(() => new Error(error.error?.message || 'Failed to ban user'));
			}),
		);
	}

	/**
	 * Unban a user
	 * @param userId User's numeric ID
	 * @returns Observable with success message
	 */
	unbanUser(userId: number): Observable<{ success: boolean; message: string }> {
		return this.http.post<{ success: boolean; message: string }>(`${this.baseUrl}/${userId}/unban`, {}).pipe(
			catchError((error) => {
				return throwError(() => new Error(error.error?.message || 'Failed to unban user'));
			}),
		);
	}

	/**
	 * Disable MFA for a user (Admin action)
	 * Used when a user loses access to their authenticator app
	 * @param userId User's numeric ID
	 * @returns Observable with success message
	 */
	disableMFA(userId: number): Observable<{ success: boolean; message: string }> {
		return this.http.post<{ success: boolean; message: string }>(`${this.baseUrl}/${userId}/disable-mfa`, {}).pipe(
			catchError((error) => {
				return throwError(() => new Error(error.error?.message || 'Failed to disable MFA'));
			}),
		);
	}
}
